import { useState } from "react";
import BreadCrumb from "../Controls/BreadCrumb";
import "../../style/scss/Admin.scss";
import EmployerSearch from "../Controls/EmployerSearch";
import SkillSearch from "../Controls/SkillSearch";
import CustomButton from "../Controls/CustomButton";
import { useNavigate, Link } from "react-router-dom";
import EmployerEditModal from "../Modals/EmployerEditModal";
import { toast } from "react-toastify";
import API from "../../API";

function formatOnetRowCounts(rowCounts) {
  if (!rowCounts || typeof rowCounts !== "object") return "";
  return Object.entries(rowCounts)
    .map(([table, count]) => `${table}: ${count}`)
    .join(", ");
}

function formatEolRowCounts(rowCounts) {
  if (!rowCounts || typeof rowCounts !== "object") return "";
  const productCount = rowCounts.product ?? rowCounts.Product;
  const releaseCount = rowCounts.release ?? rowCounts.Release;
  if (productCount == null && releaseCount == null) return "";
  return `${productCount ?? 0} products, ${releaseCount ?? 0} releases`;
}

export default function AdminDashboardView() {
  const navigate = useNavigate();
  const [employerEditId, setEmployerEditId] = useState(null);
  const [onetBusy, setOnetBusy] = useState(false);
  const [eolBusy, setEolBusy] = useState(false);
  const [eolMapBusy, setEolMapBusy] = useState(false);
  const [eolSeedBusy, setEolSeedBusy] = useState(false);
  const [eolProjectBusy, setEolProjectBusy] = useState(false);
  const [mergeCatalogBusy, setMergeCatalogBusy] = useState(false);
  const [junkCleanupBusy, setJunkCleanupBusy] = useState(false);

  const editEmployer = (value) => {
    const id = value?.ID ?? value?.id;
    if (!id) return;
    setEmployerEditId(id);
  };

  const editSkill = (value) => {
    const id = value?.ID ?? value?.id;
    if (!id) return;
    navigate("/adminSkillEdit/" + id);
  };

  async function updateOnetData() {
    if (onetBusy) return;
    setOnetBusy(true);
    try {
      const resp = await API.postAsync("/Onet/Import", {});
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const counts = formatOnetRowCounts(data.rowCounts ?? data.RowCounts);
        toast.success(
          counts
            ? `O*NET ${data.databaseVersion ?? data.DatabaseVersion ?? ""} updated (${counts})`
            : "O*NET data updated"
        );
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "O*NET import failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "O*NET import failed");
    } finally {
      setOnetBusy(false);
    }
  }

  async function updateEolData() {
    if (eolBusy) return;
    setEolBusy(true);
    try {
      const resp = await API.postAsync("/Eol/Import", {});
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const counts = formatEolRowCounts(data.rowCounts ?? data.RowCounts);
        const schemaVersion = data.schemaVersion ?? data.SchemaVersion;
        toast.success(
          counts
            ? `endoflife.date catalog updated (${counts})`
            : `endoflife.date catalog ${schemaVersion ?? ""} updated`
        );
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "endoflife.date import failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "endoflife.date import failed");
    } finally {
      setEolBusy(false);
    }
  }

  async function mapEolSkills() {
    if (eolMapBusy) return;
    setEolMapBusy(true);
    try {
      const resp = await API.postAsync("/Eol/MapSkills", {});
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const mapped = data.mapped ?? data.Mapped ?? 0;
        const ambiguous = data.ambiguous ?? data.Ambiguous ?? 0;
        const unmapped = data.unmappedProducts ?? data.UnmappedProducts ?? 0;
        toast.success(
          `Mapped ${mapped} skill(s) to endoflife.date` +
            (ambiguous ? ` (${ambiguous} ambiguous skipped)` : "") +
            (unmapped ? `; ${unmapped} products still unmapped` : "")
        );
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "endoflife.date skill mapping failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "endoflife.date skill mapping failed");
    } finally {
      setEolMapBusy(false);
    }
  }

  async function seedEolSkills() {
    if (eolSeedBusy) return;
    setEolSeedBusy(true);
    try {
      const resp = await API.postAsync("/Eol/SeedSkills", {});
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const created = data.created ?? data.Created ?? 0;
        const linked = data.linkedExisting ?? data.LinkedExisting ?? 0;
        const mapped = data.mapped ?? data.Mapped ?? 0;
        const skippedDevice = data.skippedDevice ?? data.SkippedDevice ?? 0;
        toast.success(
          `Seeded ${created} new skill(s), linked ${linked} existing; ` +
            `${mapped} map row(s)` +
            (skippedDevice ? ` (${skippedDevice} devices skipped)` : "")
        );
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "endoflife.date skill seed failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "endoflife.date skill seed failed");
    } finally {
      setEolSeedBusy(false);
    }
  }

  async function projectEolVersions() {
    if (eolProjectBusy) return;
    setEolProjectBusy(true);
    try {
      const resp = await API.postAsync("/Eol/ProjectVersions", {});
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const skills = data.skillsProjected ?? data.SkillsProjected ?? 0;
        const inserted = data.inserted ?? data.Inserted ?? 0;
        const updated = data.updated ?? data.Updated ?? 0;
        toast.success(
          `Projected versions for ${skills} skill(s): ${inserted} inserted, ${updated} updated` +
            (data.slugsBackfilled ?? data.SlugsBackfilled
              ? `; ${data.slugsBackfilled ?? data.SlugsBackfilled} slug(s) backfilled`
              : "")
        );
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "endoflife.date version projection failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "endoflife.date version projection failed");
    } finally {
      setEolProjectBusy(false);
    }
  }

  function formatMergeResult(data, applied) {
    const candidates = data.candidates ?? data.Candidates ?? 0;
    const merged = data.merged ?? data.Merged ?? 0;
    const versions = data.versionsCreated ?? data.VersionsCreated ?? 0;
    const unmatched = (data.unmatched ?? data.Unmatched ?? []).length;
    const prefix = applied ? "Merged" : "Would merge";
    return (
      `${prefix} ${merged}/${candidates} version-named skill(s)` +
      (versions ? `, ${versions} version row(s)` : "") +
      (unmatched ? `, ${unmatched} unmatched` : "")
    );
  }

  async function mergeVersionNamedSkills(dryRun) {
    if (mergeCatalogBusy) return;
    if (
      !dryRun &&
      !window.confirm(
        "Apply catalog merge? Version-named skills (e.g. Java SE 8) will be folded into base skills and marked merged."
      )
    ) {
      return;
    }
    setMergeCatalogBusy(true);
    try {
      const resp = await API.postAsync("/Skill/Versions/MergeCatalog", {
        dryRun,
        createMissingBases: true,
      });
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        toast.success(formatMergeResult(data, !dryRun));
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "Skill catalog merge failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "Skill catalog merge failed");
    } finally {
      setMergeCatalogBusy(false);
    }
  }

  async function cleanupJunkSkills(dryRun) {
    if (junkCleanupBusy) return;
    if (
      !dryRun &&
      !window.confirm(
        "Soft-delete E2E/Wave2/test skills? They will be marked Status=deleted (not hard-deleted)."
      )
    ) {
      return;
    }
    setJunkCleanupBusy(true);
    try {
      const resp = await API.postAsync("/Skill/JunkCleanup", { dryRun });
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const soft = data.softDeleted ?? data.SoftDeleted ?? 0;
        const candidates = data.candidates ?? data.Candidates ?? 0;
        toast.success(
          dryRun
            ? `Would soft-delete ${soft}/${candidates} junk skill(s)`
            : `Soft-deleted ${soft}/${candidates} junk skill(s)`
        );
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "Junk skill cleanup failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "Junk skill cleanup failed");
    } finally {
      setJunkCleanupBusy(false);
    }
  }

  async function purgeJunkSkills(dryRun) {
    if (junkCleanupBusy) return;
    if (
      !dryRun &&
      !window.confirm(
        "HARD-DELETE all junk/E2E skills and their dependents? This cannot be undone."
      )
    ) {
      return;
    }
    setJunkCleanupBusy(true);
    try {
      const resp = await API.postAsync("/Skill/JunkPurge", { dryRun });
      const data = resp?.jsonData;
      const status = data?.status ?? data?.Status;
      if (resp?.ok && status === "succeeded") {
        const purged = data.purged ?? data.Purged ?? 0;
        const failed = data.failed ?? data.Failed ?? 0;
        const candidates = data.candidates ?? data.Candidates ?? 0;
        const msg = dryRun
          ? `Would purge ${purged}/${candidates} junk skill(s)`
          : `Purged ${purged}/${candidates} junk skill(s)` +
            (failed ? `, ${failed} failed` : "");
        if (failed) toast.warn(msg);
        else toast.success(msg);
      } else {
        toast.error(
          data?.error ||
            data?.Error ||
            data?.message ||
            "Junk skill purge failed"
        );
      }
    } catch (err) {
      toast.error(err?.message || "Junk skill purge failed");
    } finally {
      setJunkCleanupBusy(false);
    }
  }

  return (
    <div>
      <BreadCrumb props={["Admin"]} />
      <div className="adminTabbody tabBody">
        <h2>Site Administration Tasks</h2>
        <div className="leftPanel">
          <ul className="admin">
            <li>
              <Link to="/adminPackageItemTypes">
                <CustomButton fullWidth={true} variant="contained" color="primary">
                  Manage Package Item Types
                </CustomButton>
              </Link>
            </li>
            <li>
              <Link to="/adminAgreements">
                <CustomButton fullWidth={true} variant="contained" color="primary">
                  Manage Agreements
                </CustomButton>
              </Link>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => window.open("/job-intake", "_blank")}
              >
                Import job
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={onetBusy}
                onClick={updateOnetData}
              >
                {onetBusy ? "Updating O*NET…" : "Update O*NET data"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={eolBusy}
                onClick={updateEolData}
              >
                {eolBusy
                  ? "Updating endoflife.date…"
                  : "Update endoflife.date catalog"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={eolMapBusy}
                onClick={mapEolSkills}
              >
                {eolMapBusy
                  ? "Mapping endoflife.date skills…"
                  : "Map endoflife.date skills"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={eolSeedBusy}
                onClick={seedEolSkills}
              >
                {eolSeedBusy
                  ? "Seeding missing skills…"
                  : "Seed missing skills from endoflife.date"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={eolProjectBusy}
                onClick={projectEolVersions}
              >
                {eolProjectBusy
                  ? "Projecting skill versions…"
                  : "Project skill versions from endoflife.date"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={mergeCatalogBusy}
                onClick={() => mergeVersionNamedSkills(true)}
              >
                {mergeCatalogBusy
                  ? "Merging version-named skills…"
                  : "Preview merge version-named skills"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={mergeCatalogBusy}
                onClick={() => mergeVersionNamedSkills(false)}
              >
                Apply merge version-named skills
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={junkCleanupBusy}
                onClick={() => cleanupJunkSkills(true)}
              >
                {junkCleanupBusy
                  ? "Cleaning junk skills…"
                  : "Preview soft-delete junk/E2E skills"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={junkCleanupBusy}
                onClick={() => cleanupJunkSkills(false)}
              >
                Soft-delete junk/E2E skills
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={junkCleanupBusy}
                onClick={() => purgeJunkSkills(true)}
              >
                {junkCleanupBusy
                  ? "Purging junk skills…"
                  : "Preview purge junk/E2E skills"}
              </CustomButton>
            </li>
            <li>
              <CustomButton
                fullWidth
                variant="contained"
                color="primary"
                disabled={junkCleanupBusy}
                onClick={() => purgeJunkSkills(false)}
              >
                Purge junk/E2E skills
              </CustomButton>
            </li>
          </ul>
        </div>

        <div className="rightPanel">
          <ul className="admin">
            <li>
              <EmployerSearch label="Edit Employer/Company" onSelect={editEmployer} />
            </li>
            <li>
              <SkillSearch label="Edit Skill" onSelect={editSkill} />
            </li>
          </ul>
        </div>
      </div>
      {employerEditId != null ? (
        <EmployerEditModal
          employerId={employerEditId}
          onClose={() => setEmployerEditId(null)}
        />
      ) : null}
    </div>
  );
}
